import { useState, useCallback } from 'react'
import type { CalculatorContent } from '@/types/content'

type Currency = 'INR' | 'USD'

export interface CalcResult {
  totalHrs: number
  yearlyCost: number
  returnedHrs: number
  savedCost: number
  score: number
  scoreLabel: string
  insight: string
}

export function useCalculator(content: CalculatorContent) {
  const [currency, setCurrency] = useState<Currency>('INR')
  const [teamSize, setTeamSize] = useState(5)
  const [hoursPerPerson, setHoursPerPerson] = useState(12)
  const [hourlyRate, setHourlyRate] = useState(content.currency.INR.default)
  const [sector, setSector] = useState('legal')
  const [selectedTasks, setSelectedTasks] = useState<Set<number>>(new Set())
  const [result, setResult] = useState<CalcResult | null>(null)

  const config = content.currency[currency]

  const switchCurrency = useCallback((c: Currency) => {
    setCurrency(c)
    setHourlyRate(content.currency[c].default)
  }, [content.currency])

  const toggleTask = useCallback((index: number) => {
    setSelectedTasks((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }, [])

  const getTaskRate = useCallback((): number => {
    if (selectedTasks.size === 0) {
      return content.sectorRates[sector] || 0.6
    }
    const rates = [...selectedTasks].map((i) => content.tasks[i].rate)
    return Math.round((rates.reduce((a, b) => a + b, 0) / rates.length) * 100) / 100
  }, [selectedTasks, sector, content.sectorRates, content.tasks])

  const calculate = useCallback(() => {
    const autoRate = getTaskRate()
    const sym = config.symbol
    const totalHrs = teamSize * hoursPerPerson
    const yearlyHrs = totalHrs * 52
    const yearlyCost = yearlyHrs * hourlyRate
    const returnedHrs = Math.round(totalHrs * autoRate)
    const savedCost = Math.round(yearlyCost * autoRate)
    const score = Math.round(autoRate * 100)

    let scoreLabel: string
    let insight: string
    const taskNames =
      selectedTasks.size > 0
        ? [...selectedTasks]
            .slice(0, 2)
            .map((i) => content.tasks[i].label)
            .join(' and ')
        : 'manual processing'

    if (score >= 70) {
      scoreLabel = 'High — Strong Fit'
      insight = `Your workflow has ${score}% automation potential. Tasks like ${taskNames} are well-understood by AI systems. A targeted build could return ${returnedHrs} hours to your team every week — that's ${sym}${savedCost.toLocaleString()} back in your budget annually.`
    } else if (score >= 58) {
      scoreLabel = 'Medium — Good Fit'
      insight = `Your workflow has ${score}% automation potential. Some tasks require human judgement, but the high-volume, repetitive elements are automatable. We'd expect to return ${returnedHrs} hours per week with a well-scoped implementation.`
    } else {
      scoreLabel = "Exploratory — Let's Talk"
      insight = `Your workflow has elements that benefit from human oversight, but AI can still handle the research, data gathering, and reporting layers. We'd want a closer look before committing to a number.`
    }

    setResult({ totalHrs, yearlyCost, returnedHrs, savedCost, score, scoreLabel, insight })
  }, [teamSize, hoursPerPerson, hourlyRate, getTaskRate, config.symbol, selectedTasks, content.tasks])

  return {
    currency,
    switchCurrency,
    teamSize,
    setTeamSize,
    hoursPerPerson,
    setHoursPerPerson,
    hourlyRate,
    setHourlyRate,
    sector,
    setSector,
    selectedTasks,
    toggleTask,
    config,
    calculate,
    result,
  }
}
